import { Spacing } from '@persona-id/design-tokens';
import { Selection } from '@react-types/shared';
import { RulesLogic } from 'json-logic-js';
import { useState } from 'react';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { DEFAULT_LOGIC_RULE_BLOCK } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import { SectionAccordion } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/SectionAccordion';
import ValidationEditorPanel from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ValidationsEditor/ValidationEditorPanel';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import ListBox from 'design-system/components/ListBox/ListBox';
import { paragraphMixins } from 'design-system/mixins/text';
import { InquiryIso6391LanguageCode } from 'lib/constants/locales';
import { convertFieldSchemasToJsonSchema, FieldSchema } from 'lib/fields';
import { UiComponentConfigRule, UiFormFieldComponentConfig } from 'lib/interfaces/ui-components';
import { JsonSchemaObject, JsonSchemaTypes } from 'lib/json-schema';

const PLACEHOLDER_VALIDATION_MESSAGE = 'Invalid Input';
const DEFAULT_VALIDATION: UiComponentConfigRule = {
  logic: DEFAULT_LOGIC_RULE_BLOCK.rules as RulesLogic,
  'message-default-value': PLACEHOLDER_VALIDATION_MESSAGE,
  'message-translations': [{ locale: 'en-US', value: PLACEHOLDER_VALIDATION_MESSAGE }],
};

const getValidationJsonSchema = (referencedField: FieldSchema | undefined): JsonSchemaObject => {
  if (referencedField == null)
    return {
      type: JsonSchemaTypes.Object,
      properties: {},
    };
  const fieldJsonSchema = convertFieldSchemasToJsonSchema([referencedField]);
  return {
    type: JsonSchemaTypes.Object,
    properties: {
      value: fieldJsonSchema.properties[referencedField.key],
    },
  };
};

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const EmptyStateButton = styled.button`
  ${paragraphMixins.md};
  width: 100%;
  text-align: start;
  color: ${({ theme }) => theme.color.text.secondary};
  padding: ${Spacing[1]} ${Spacing[2]};
  background-color: transparent;
  outline: none;
  border: none;
  &:hover:not(&:disabled) {
    cursor: pointer;
  }
`;

interface ValidationsEditorProps {
  componentConfig: UiFormFieldComponentConfig;
  fieldSchema: FieldSchema[];
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  editable: boolean;
  handleValidationsUpdate: (validationsUpdate: Record<string, any>) => void;
}

const ValidationsEditor = ({
  componentConfig,
  fieldSchema,
  currentLocale,
  enabledLocales,
  editable,
  handleValidationsUpdate,
}: ValidationsEditorProps) => {
  const hasAccessForEnhancedTier = useHasAccessForTierDeterminator()(
    EditorAccessTier.InquiriesEnhanced,
  );
  const isEditable = editable && hasAccessForEnhancedTier;
  const { field, validations } = componentConfig.config;
  const [currentValidationIndex, setCurrentValidationIndex] = useState<number | null>(null);

  const items: Array<{ name: string; key: string }> = (validations ?? []).map(
    (validation, index) => {
      const { 'message-default-value': defaultValue, 'message-translations': translations } =
        validation;
      const currentTranslation = (translations ?? []).find(
        ({ locale }) => locale === currentLocale,
      );
      return {
        name:
          (currentTranslation?.value as string) ?? defaultValue ?? PLACEHOLDER_VALIDATION_MESSAGE,
        key: `${index}`,
      };
    },
  );

  const handleAddValidation = () => {
    handleValidationsUpdate({
      validations: [...(validations ?? []), DEFAULT_VALIDATION],
    });
    setCurrentValidationIndex(items.length);
  };

  const handleDeleteValidation = () => {
    handleValidationsUpdate({
      validations: (validations ?? []).filter((_, index) => index !== currentValidationIndex),
    });
    setCurrentValidationIndex(null);
  };

  const handleUpdateValidation = (updatedValidation: UiComponentConfigRule) => {
    handleValidationsUpdate({
      validations: (validations ?? []).map((validation, index) =>
        index === currentValidationIndex ? updatedValidation : validation,
      ),
    });
  };

  const validationJsonSchema = getValidationJsonSchema(
    fieldSchema.find(({ key }) => key === field),
  );

  return (
    <SectionAccordion
      title={
        <HorizontalContainer>
          {!hasAccessForEnhancedTier && (
            <ProductUpsellTooltip
              side='left'
              learnMoreId='component-panel__validations-editor'
              accessTier={EditorAccessTier.InquiriesEnhanced}
              trigger={
                <div>
                  <InquiriesGatingSticker
                    dataMarketingIdSuffix='validations-editor'
                    stickerType={EnhancedGatingStickerType.Enhanced}
                    iconOnly
                  />
                </div>
              }
            />
          )}
          Validations
        </HorizontalContainer>
      }
    >
      <ListBox
        aria-label='Input validation rules'
        selectionMode='single'
        selectionBehavior='toggle'
        items={items}
        disabledKeys={isEditable ? [] : items.map(({ key }) => key)}
        selectedKeys={currentValidationIndex ? [currentValidationIndex.toString()] : []}
        renderEmptyState={() => (
          <EmptyStateButton
            as='button'
            type='button'
            id='validations-list-box__empty-state-button'
            aria-disabled={!isEditable}
            disabled={!isEditable}
            onClick={handleAddValidation}
          >
            {isEditable ? 'Click to add a new validation rule' : 'No validation rules configured'}
          </EmptyStateButton>
        )}
        onAdd={isEditable ? handleAddValidation : undefined}
        onSelectionChange={(selection: Selection) => {
          if (selection !== 'all') {
            const validationIndex = selection.values().next().value;
            setCurrentValidationIndex(Number(validationIndex));
          }
        }}
        disallowEmptySelection
        fullWidth
      >
        {items.map((item) => (
          <ListBox.Item key={item.key} id={item.key} aria-label={`Listbox item for '${item.name}'`}>
            {item.name}
          </ListBox.Item>
        ))}
      </ListBox>
      {currentValidationIndex != null && validations != null && (
        <ValidationEditorPanel
          validation={validations[currentValidationIndex]}
          componentName={componentConfig.name}
          currentLocale={currentLocale}
          enabledLocales={enabledLocales}
          jsonSchema={validationJsonSchema}
          editable={isEditable}
          onOutsideClick={() => setCurrentValidationIndex(null)}
          updateValidation={handleUpdateValidation}
          deleteValidation={handleDeleteValidation}
        />
      )}
    </SectionAccordion>
  );
};

export default ValidationsEditor;
